export type CCASubdomain =
  | 'Abstract Reasoning'
  | 'Logical Reasoning'
  | 'Quantitative Aptitude'
  | 'Verbal Ability'
  | 'Data Interpretation';

export type CCAQuestionType = 'short-answer' | 'multiple-choice';
export type CCAQuestionFormat = 'wordy' | 'image';

export interface CCAQuestion {
  id: string;
  subdomain: CCASubdomain;
  level: number; // 1-10
  questionType: CCAQuestionType;
  questionText: string;
  idealAnswer: string;
  choices?: string[];
  videoRequired: boolean;
  questionFormat: CCAQuestionFormat;
}

export const CCA_SUBDOMAINS: CCASubdomain[] = [
  'Abstract Reasoning',
  'Logical Reasoning',
  'Quantitative Aptitude',
  'Verbal Ability',
  'Data Interpretation',
];

export const CCA_LEVEL_LABELS: Record<number, string> = {
  1: 'Sub-school',
  2: 'Sub-school',
  3: 'School',
  4: 'School',
  5: 'Early Tertiary',
  6: 'Graduate',
  7: 'Professional',
  8: 'Senior Professional',
  9: 'Advanced / Specialist',
  10: 'Elite / Strategic',
};

// Generate 4 dummy questions per subdomain per level (representing 50 pre-defined)
const generateQuestions = (): CCAQuestion[] => {
  const questions: CCAQuestion[] = [];
  let idCounter = 1;

  const subdomainTemplates: Record<
    CCASubdomain,
    Array<{
      type: CCAQuestionType;
      format: CCAQuestionFormat;
      textFn: (level: number) => string;
      idealFn: (level: number) => string;
      choices?: string[];
    }>
  > = {
    'Abstract Reasoning': [
      {
        type: 'multiple-choice',
        format: 'wordy',
        textFn: (l) =>
          `[Level ${l}] Which shape completes the sequence: Circle, Square, Triangle, Circle, Square, ___?`,
        idealFn: () => 'Triangle',
        choices: ['Circle', 'Square', 'Triangle', 'Diamond'],
      },
      {
        type: 'short-answer',
        format: 'wordy',
        textFn: (l) =>
          `[Level ${l}] In a pattern where each term doubles and adds 1: 1, 3, 7, 15, ___. What is the next number?`,
        idealFn: () => '31',
      },
      {
        type: 'multiple-choice',
        format: 'image',
        textFn: (l) =>
          `[Level ${l}] Look at the pattern of shapes. Which option continues the sequence correctly?`,
        idealFn: () => 'Option B — rotated 90° clockwise',
        choices: ['Option A', 'Option B', 'Option C', 'Option D'],
      },
      {
        type: 'short-answer',
        format: 'wordy',
        textFn: (l) =>
          `[Level ${l}] If all Bloops are Razzles, and all Razzles are Lazzles, are all Bloops definitely Lazzles?`,
        idealFn: () => 'Yes, because Bloops → Razzles → Lazzles by transitivity.',
      },
    ],
    'Logical Reasoning': [
      {
        type: 'multiple-choice',
        format: 'wordy',
        textFn: (l) =>
          `[Level ${l}] All managers are leaders. Some leaders are visionaries. Which conclusion must be true?`,
        idealFn: () => 'Some managers may be visionaries.',
        choices: [
          'All managers are visionaries',
          'No managers are visionaries',
          'Some managers may be visionaries',
          'All visionaries are managers',
        ],
      },
      {
        type: 'short-answer',
        format: 'wordy',
        textFn: (l) =>
          `[Level ${l}] Five people stand in a line: A is before B, C is after D, B is before C. Who is in the middle?`,
        idealFn: () => 'B is in the middle (order: A, B, C with D before C).',
      },
      {
        type: 'multiple-choice',
        format: 'wordy',
        textFn: (l) =>
          `[Level ${l}] If no X are Y, and some Z are X, which statement is definitely true?`,
        idealFn: () => 'Some Z are not Y',
        choices: [
          'All Z are Y',
          'Some Z are not Y',
          'No Z are Y',
          'All Y are Z',
        ],
      },
      {
        type: 'short-answer',
        format: 'wordy',
        textFn: (l) =>
          `[Level ${l}] A clock shows 3:15. What is the angle between the hour and minute hands?`,
        idealFn: () => '7.5 degrees',
      },
    ],
    'Quantitative Aptitude': [
      {
        type: 'multiple-choice',
        format: 'wordy',
        textFn: (l) =>
          `[Level ${l}] A train travels 360 km in 4 hours. What is its average speed in km/h?`,
        idealFn: () => '90 km/h',
        choices: ['80 km/h', '90 km/h', '100 km/h', '75 km/h'],
      },
      {
        type: 'short-answer',
        format: 'wordy',
        textFn: (l) =>
          `[Level ${l}] If 20% of a number is 80, what is 35% of the same number?`,
        idealFn: () => '140',
      },
      {
        type: 'multiple-choice',
        format: 'wordy',
        textFn: (l) =>
          `[Level ${l}] The ratio of boys to girls in a class is 3:5. If there are 40 students total, how many are boys?`,
        idealFn: () => '15',
        choices: ['10', '15', '20', '25'],
      },
      {
        type: 'short-answer',
        format: 'image',
        textFn: (l) =>
          `[Level ${l}] A rectangle has a perimeter of 40cm and a width of 8cm. What is its area?`,
        idealFn: () => '96 cm² (length = 12cm, area = 12 × 8)',
      },
    ],
    'Verbal Ability': [
      {
        type: 'multiple-choice',
        format: 'wordy',
        textFn: (l) =>
          `[Level ${l}] Choose the word most similar in meaning to "Ephemeral":`,
        idealFn: () => 'Transient',
        choices: ['Permanent', 'Transient', 'Abundant', 'Deliberate'],
      },
      {
        type: 'short-answer',
        format: 'wordy',
        textFn: (l) =>
          `[Level ${l}] Identify and correct the grammatical error: "The team are winning their matches consistently."`,
        idealFn: () =>
          '"The team is winning its matches consistently." — collective noun takes singular verb.',
      },
      {
        type: 'multiple-choice',
        format: 'wordy',
        textFn: (l) =>
          `[Level ${l}] Which sentence uses the word "infer" correctly?`,
        idealFn: () =>
          'From the data, we can infer that sales are declining.',
        choices: [
          'The teacher will infer the answer to the students.',
          'From the data, we can infer that sales are declining.',
          'He inferred his opinion loudly.',
          'She inferred the report to her manager.',
        ],
      },
      {
        type: 'short-answer',
        format: 'wordy',
        textFn: (l) =>
          `[Level ${l}] Rearrange these words to form a coherent sentence: "quickly / the / ran / fox / fence / over / the"`,
        idealFn: () => 'The fox quickly ran over the fence.',
      },
    ],
    'Data Interpretation': [
      {
        type: 'multiple-choice',
        format: 'image',
        textFn: (l) =>
          `[Level ${l}] A bar chart shows quarterly sales: Q1=120, Q2=150, Q3=130, Q4=180. Which quarter had the highest growth rate vs. prior quarter?`,
        idealFn: () => 'Q4 (38% growth from Q3)',
        choices: ['Q1', 'Q2', 'Q3', 'Q4'],
      },
      {
        type: 'short-answer',
        format: 'wordy',
        textFn: (l) =>
          `[Level ${l}] A pie chart shows expenses: Rent 40%, Salaries 35%, Marketing 15%, Other 10%. If total expenses are $200,000, how much is spent on Salaries?`,
        idealFn: () => '$70,000',
      },
      {
        type: 'multiple-choice',
        format: 'wordy',
        textFn: (l) =>
          `[Level ${l}] A table shows 5 years of revenue: 100, 120, 115, 140, 160 ($ thousands). What is the mean revenue?`,
        idealFn: () => '$127,000',
        choices: ['$120,000', '$127,000', '$130,000', '$115,000'],
      },
      {
        type: 'short-answer',
        format: 'image',
        textFn: (l) =>
          `[Level ${l}] A line graph shows employee headcount declining from 500 to 350 over 5 years. What is the average annual reduction?`,
        idealFn: () => '30 employees per year',
      },
    ],
  };

  for (const subdomain of CCA_SUBDOMAINS) {
    for (let level = 1; level <= 10; level++) {
      const templates = subdomainTemplates[subdomain];
      templates.forEach((template) => {
        questions.push({
          id: `cca-${idCounter++}`,
          subdomain,
          level,
          questionType: template.type,
          questionText: template.textFn(level),
          idealAnswer: template.idealFn(level),
          choices: template.choices,
          videoRequired: true,
          questionFormat: template.format,
        });
      });
    }
  }

  return questions;
};

export const CCA_QUESTIONS: CCAQuestion[] = generateQuestions();

// Dummy candidate CCA results for applicant analysis
export interface CandidateCCAResult {
  candidateId: string;
  status: 'invite-sent' | 'in-progress' | 'completed';
  totalScore?: number; // 0-100
  subdomainScores?: Record<CCASubdomain, number>;
  completedAt?: string;
  questionResults?: Array<{
    questionId: string;
    questionText: string;
    candidateAnswer: string;
    idealAnswer: string;
    score: number; // 0-100
    timeTaken: number; // seconds
  }>;
}

export const DUMMY_CCA_RESULTS: Record<string, CandidateCCAResult> = {
  'candidate-1': {
    candidateId: 'candidate-1',
    status: 'completed',
    totalScore: 78,
    subdomainScores: {
      'Abstract Reasoning': 82,
      'Logical Reasoning': 75,
      'Quantitative Aptitude': 80,
      'Verbal Ability': 70,
      'Data Interpretation': 85,
    },
    completedAt: '2026-04-28T10:30:00Z',
    questionResults: [
      {
        questionId: 'cca-1',
        questionText: '[Level 6] Which shape completes the sequence?',
        candidateAnswer: 'Triangle',
        idealAnswer: 'Triangle',
        score: 100,
        timeTaken: 42,
      },
      {
        questionId: 'cca-5',
        questionText: '[Level 6] All managers are leaders...',
        candidateAnswer: 'Some managers may be visionaries',
        idealAnswer: 'Some managers may be visionaries',
        score: 100,
        timeTaken: 55,
      },
      {
        questionId: 'cca-9',
        questionText: '[Level 6] A train travels 360 km in 4 hours...',
        candidateAnswer: '90 km/h',
        idealAnswer: '90 km/h',
        score: 100,
        timeTaken: 30,
      },
      {
        questionId: 'cca-13',
        questionText: '[Level 6] Choose the word most similar to Ephemeral',
        candidateAnswer: 'Permanent',
        idealAnswer: 'Transient',
        score: 0,
        timeTaken: 20,
      },
      {
        questionId: 'cca-17',
        questionText: '[Level 6] Bar chart quarterly sales question',
        candidateAnswer: 'Q4',
        idealAnswer: 'Q4',
        score: 100,
        timeTaken: 65,
      },
      {
        questionId: 'cca-2',
        questionText: '[Level 6] Pattern doubling sequence',
        candidateAnswer: '31',
        idealAnswer: '31',
        score: 100,
        timeTaken: 48,
      },
    ],
  },
  'candidate-2': {
    candidateId: 'candidate-2',
    status: 'in-progress',
    completedAt: undefined,
  },
  'candidate-3': {
    candidateId: 'candidate-3',
    status: 'invite-sent',
    completedAt: undefined,
  },
  'candidate-4': {
    candidateId: 'candidate-4',
    status: 'completed',
    totalScore: 62,
    subdomainScores: {
      'Abstract Reasoning': 55,
      'Logical Reasoning': 68,
      'Quantitative Aptitude': 60,
      'Verbal Ability': 72,
      'Data Interpretation': 58,
    },
    completedAt: '2026-04-30T14:15:00Z',
    questionResults: [],
  },
  'candidate-5': {
    candidateId: 'candidate-5',
    status: 'completed',
    totalScore: 91,
    subdomainScores: {
      'Abstract Reasoning': 95,
      'Logical Reasoning': 90,
      'Quantitative Aptitude': 88,
      'Verbal Ability': 93,
      'Data Interpretation': 89,
    },
    completedAt: '2026-05-01T09:00:00Z',
    questionResults: [],
  },
};
